import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, EventEmitter, forwardRef, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import Debug from 'debug';
import { map, Observable, startWith } from 'rxjs';
import { AumFormField } from './../aumFormField';

const debug = Debug('aumico:aumico-lib-core-fe:AumSelect');

export interface AumSelectChange extends Partial<MatSelectChange> {
    value: INameId | INameId[];
}

/**
 * to overwrite the with you have to change the css of .mat-form-field-infix
 */
@Component({
    selector: 'aum-select',
    styleUrls: ['../aumFormField/aum-form-field.scss', '../aumOption/option.scss', './aum-select.scss'],
    templateUrl: './aum-select.html',
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AumSelect),
            multi: true,
        },
    ],
    host: {
        class: 'aum-form-field aum-select ',
    },
    inputs: ['aumFormControl', 'size', 'floatLabel'],
})
export class AumSelect extends AumFormField implements OnInit {
    filterCtrl = new UntypedFormControl();
    filteredOptions: Observable<INameId[] | any[]>;

    @Input() options: INameId[] | any[];
    @Input() override placeholder: string;
    @Input() propertyOfOptionToDisplay: string;
    @Input() propertyOfOptionId = 'id';
    @Input() language: string;
    @Input() defaultLanguage: string;
    @Input() aumFormControlName?: string;
    @Input() hidden = false;
    @Input() selectedValue: string;
    @Input() control: UntypedFormControl = new UntypedFormControl();
    @Input() useIndependentControl = false;
    @Input() searchEnabled = false;
    @Input() simpleSelect = false;

    /** Whether the component is in multiple selection mode. */
    private _multiple = false;

    @Input()
    get multiple(): boolean {
        return this._multiple;
    }
    set multiple(value: boolean) {
        this._multiple = coerceBooleanProperty(value);
    }

    @Output() selectionChange = new EventEmitter<AumSelectChange>();
    selectionChangeEventHandler($event: MatSelectChange) {
        this.selectionChange.emit($event as AumSelectChange);
    }
    /**
     * Function to compare the option values with the selected values. The first argument
     * is a value from an option. The second is a value from the selection. A boolean
     * should be returned.
     */
    @Input()
    get compareWith() {
        return this._compareWith;
    }
    set compareWith(fn: (o1: any, o2: any) => boolean) {
        if (fn != null && typeof fn !== 'function') {
            throw new Error('Compare with must be a function');
        }
        this._compareWith = fn;
    }

    ngOnInit() {
        this.filteredOptions = this.filterCtrl.valueChanges.pipe(
            startWith(''),
            map((value) => this.filterOptions(value)),
        );
    }

    private filterOptions(value: string): any[] {
        const filterValue = value.toLowerCase();

        if (this.propertyOfOptionToDisplay != null && this.language == null) {
            return this.options.filter((option) =>
                option[this.propertyOfOptionToDisplay].name.toLowerCase().includes(filterValue),
            );
        }

        if (this.propertyOfOptionToDisplay != null && this.language != null && this.defaultLanguage != null) {
            return this.options.filter((option) =>
                (option[this.propertyOfOptionToDisplay][this.language] != null
                    ? option[this.propertyOfOptionToDisplay][this.language]
                    : option[this.propertyOfOptionToDisplay][this.defaultLanguage]
                )
                    .toLowerCase()
                    .includes(filterValue),
            );
        }
        return this.options.filter((option) => option.name.toLowerCase().includes(filterValue));
    }

    /** Comparison function to specify which option is displayed. Defaults to object equality. */
    private _compareWith = (o1: INameId, o2: INameId) => {
        return o1?.id === o2?.id;
    };
}
